import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import React, { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { Badge } from "../../utils/local-antd";
import { Flex } from "../../components/Flex";
import { SubHeader } from "../../components/SubHeader";
import { Spacing } from "../../theme/Spacing";
import { footerActions } from "../../redux/footer-daily-stats/footer-daily-stats.slice";
import { permissionActions } from "../../redux/permission/permission.slice";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { taskActions } from "../../redux/task/task.slice";
import { taskSliceSelectors } from "../../redux/task/task.selector";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { TasksTable } from "./TasksTable";
import { TasksSettingsDrawer } from "./components/TasksSettingsDrawer";
import { faGear } from "@fortawesome/free-solid-svg-icons/faGear";
import { useDrawersContext } from "./drawers-context";
import { TaskKind } from "../../model/task/shared/TasksKind.enum";
import { EntityIcons } from "../../utils/GlobalIcons";
import { AtiraDropdown } from "../../components/AtiraDropdown";
import { Button } from "../../components/Button";
import { faBars } from "@fortawesome/free-solid-svg-icons/faBars";
import { MenuProps } from "../../utils/local-antd";
import { AtiraIcon } from "../../components/AtiraIcon";
import { Text } from "../../components/Text";
import { TestIDs } from "../../tests/types/testIds";

const TasksRoute: React.FC = () => {
  const [tasksSettingsDrawerVisible, setTasksSettingsDrawerVisible] =
    useState(false);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const isOrgMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const allTasksMeta = useAppSelector(taskSliceSelectors.selectAllTasksMeta);

  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { setTaskCreateVisible, setTaskKind } = useDrawersContext();

  const permissions = user?.permissions?.permissions;

  const subHeaderActions: MenuProps["items"] = useMemo(() => {
    const openCreateTask = () => {
      setTaskKind(TaskKind.ORPHAN);
      setTaskCreateVisible(true);
    };

    return [
      {
        key: "tasks.create",
        label: (
          <Flex
            alignItems="center"
            gap="s"
            dataTestId={TestIDs.tasks.subHeader.dropdown.option("tasks.create")}
          >
            <AtiraIcon icon={faPlus} color="main" />
            <Text color="main">{t("common.create")}</Text>
          </Flex>
        ),
        onClick: openCreateTask,
        hide: permissions?.ORPHAN_TASK?.CREATE !== true,
      },
      {
        key: "tasks.settings",
        label: (
          <Flex
            alignItems="center"
            gap="s"
            dataTestId={TestIDs.tasks.subHeader.dropdown.option(
              "tasks.settings",
            )}
          >
            <AtiraIcon icon={faGear} color="main" />
            <Text color="main">{t("common.settings")}</Text>
          </Flex>
        ),
        onClick: () => setTasksSettingsDrawerVisible(true),
        hide: isOrgMember === true,
      },
    ].filter((action) => !action.hide);
  }, [
    isOrgMember,
    permissions?.ORPHAN_TASK?.CREATE,
    setTaskCreateVisible,
    setTaskKind,
    t,
  ]);

  useEffect(() => {
    dispatch(taskActions.getTasksSettings({ userId }));
    dispatch(permissionActions.getMembers({ userId }));
    dispatch(footerActions.getFooterDailyStats({ userId }));
  }, [dispatch, userId]);

  return (
    <Flex flexDirection="column">
      <SubHeader
        title={
          <Trans
            i18nKey="subheader.tasks"
            components={[
              <Badge
                style={{ marginBlockEnd: Spacing.s }}
                count={allTasksMeta?.total || 0}
                color={theme.main}
                overflowCount={999999}
              />,
            ]}
          />
        }
        icon={EntityIcons.Tasks}
        extra={
          subHeaderActions.length ? (
            <AtiraDropdown
              items={subHeaderActions}
              placement="bottomRight"
              trigger={["click"]}
              destroyOnHidden
              dataTestId={TestIDs.tasks.subHeader.dropdown.open}
            >
              <Button
                icon={faBars}
                color="black"
                padding="s"
                backgroundColor="white"
                hoverBackgroundColor="lightMain"
                hover
              >
                {t("common.options")}
              </Button>
            </AtiraDropdown>
          ) : null
        }
      />

      <TasksTable />

      <TasksSettingsDrawer
        open={tasksSettingsDrawerVisible}
        onClose={() => setTasksSettingsDrawerVisible(false)}
      />
    </Flex>
  );
};

export default TasksRoute;
